/* W72 STEP 8 — `Your data`: the pure half of the Account's export page. The export itself is built by the
 * `export-data` edge function AS THE CALLER (`supabase/functions/export-data/index.ts`); this module only says what
 * the page shows and how the request and the answer are spelled. */

export type ExportKind = "library" | "client-history";
export type ExportShape = "json" | "csv";

/** What the request carries. The OWNER is never in it: the function takes the coach from the signed-in session. */
export function exportRequest(kind: ExportKind, shape: ExportShape, clientId: string | null): Record<string, string> {
  if (kind === "client-history") {
    if (clientId === null || clientId === "") throw new Error("A client's history needs a client.");
    return { kind, shape, clientId };
  }
  return { kind, shape };
}

export type LibraryFileCounts = { exercises: number | null; workouts: number | null; programs: number | null; equipment: number | null };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The library row's second line: what is IN the file (hidden and archived rows included — the file is everything). */
export function libraryLine(c: LibraryFileCounts): string {
  const parts = [
    c.exercises === null ? null : plural(c.exercises, "exercise", "exercises"),
    c.workouts === null ? null : plural(c.workouts, "workout", "workouts"),
    c.programs === null ? null : plural(c.programs, "program", "programs"),
    c.equipment === null ? null : plural(c.equipment, "piece of your own equipment", "pieces of your own equipment"),
  ].filter((x): x is string => x !== null);
  return parts.length === 0 ? "Every exercise, workout, program and piece of equipment — every set, rule and note"
    : `${parts.join(" · ")} — every set, rule and note`;
}

/** The file name the server chose (RFC 6266: `filename*` wins over `filename`), or a fallback. */
export function dispositionName(header: string | null, fallback: string): string {
  if (header === null) return fallback;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) { try { return decodeURIComponent(star[1]!.trim()); } catch { /* fall through */ } }
  const plain = /filename\s*=\s*"([^"]+)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  return plain ? plain[1]!.trim() : fallback;
}

export function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type PickableClient = { id: string; name: string };

/** The client search: every word typed must start a word of the name, accents ignored (Greek names typed
 *  without their tonos still match). Sorted by name. */
export function clientMatches(clients: readonly PickableClient[], query: string): PickableClient[] {
  const fold = (s: string) => s.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
  const words = fold(query).split(/\s+/).filter((w) => w !== "");
  return clients
    .filter((c) => {
      const nameWords = fold(c.name).split(/[\s-]+/);
      return words.every((w) => nameWords.some((n) => n.startsWith(w)));
    })
    .sort((a, b) => (fold(a.name) < fold(b.name) ? -1 : fold(a.name) > fold(b.name) ? 1 : a.id < b.id ? -1 : 1));
}

/** What a failed download says. A 401 is the SESSION (the platform answers `{ message }`, the function `{ error }`),
 *  so it asks for a sign-in rather than quoting "Invalid JWT" at a coach; anything else passes the server's sentence on. */
export function failureText(status: number, body: { error?: unknown; message?: unknown } | null): string {
  if (status === 401) return "Your sign-in has expired — sign in again, then download.";
  const said = typeof body?.error === "string" ? body.error : typeof body?.message === "string" ? body.message : null;
  return said ?? `The download failed (${status}).`;
}
